import {
  useEffect,
  useRef,
  type HTMLAttributes,
  type RefObject,
  type ReactNode,
} from "react";

type PopoverPlacement = "inline" | "rail";
type PopoverAlign = "start" | "end";
type PopoverSize = "default" | "wide";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface PopoverProps extends HTMLAttributes<HTMLDivElement> {
  align?: PopoverAlign;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  isOpen: boolean;
  onDismiss: () => void;
  placement?: PopoverPlacement;
  returnFocusOnDismiss?: boolean;
  size?: PopoverSize;
}

export function Popover({
  align = "start",
  anchorRef,
  children,
  className,
  isOpen,
  onDismiss,
  placement = "inline",
  returnFocusOnDismiss = true,
  role = "dialog",
  size = "default",
  ...props
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen && wasOpenRef.current && returnFocusOnDismiss) {
      anchorRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [anchorRef, isOpen, returnFocusOnDismiss]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.stopPropagation();
        onDismiss();
      }
    };

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (panelRef.current?.contains(target)) {
        return;
      }
      if (anchorRef.current?.contains(target)) {
        return;
      }
      onDismiss();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [anchorRef, isOpen, onDismiss]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classes(
        [
          "marker-popover",
          `marker-popover--${placement}`,
          `marker-popover--${align}`,
          `marker-popover--${size}`,
        ],
        className,
      )}
      ref={panelRef}
      role={role}
      {...props}
    >
      {children}
    </div>
  );
}
